/**
 * @file statementOrder.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-02
 * @modified 2026-10-02
 *
 * Sorting and naming a statement run's rows on the Generate Statements screen
 * so they match the printer.
 *
 * The server prints (and emails, and saves) a run in one order: account number,
 * customers with NO account number last, then name (routes/statements.js,
 * utils/statementLabels on the server). The screen used `a < b`, which turns a
 * missing account number into 0 and so put those customers FIRST — the
 * opposite end. On 2026-10-01 a reprint was set up by unapproving "pages 1–5"
 * of the screen to skip what had already printed; pages 1–2 were the 25
 * no-account customers, which had not printed, and their invoices were left
 * out of the reprint as well.
 *
 * Each row now carries `printOrder` (its position in the server's order), and
 * "Acc #" sorts by it, so page 1 here is what prints first. Blank values sort
 * last in either direction, on every column.
 */

const isBlank = (v) => v == null || (typeof v === 'string' && v.trim() === '');

/** A comparator for the statements table: (field, 'asc'|'desc') → (a, b) → number. */
export function compareStatements(field, direction = 'asc') {
  const dir = direction === 'desc' ? -1 : 1;
  return (a, b) => {
    const av = a?.[field];
    const bv = b?.[field];
    const ab = isBlank(av);
    const bb = isBlank(bv);
    if (ab !== bb) return ab ? 1 : -1;
    const ap = a?.printOrder;
    const bp = b?.printOrder;
    const haveOrder = ap != null && bp != null;
    if (!ab) {
      if (field === 'accountNo' && haveOrder) return (ap - bp) * dir;
      if (av < bv) return -dir;
      if (av > bv) return dir;
    }
    // Ties keep the print order; without it, name then id.
    if (haveOrder) return ap - bp;
    const an = String(a?.account || '');
    const bn = String(b?.account || '');
    if (an !== bn) return an < bn ? -1 : 1;
    return (Number(a?.id) || 0) - (Number(b?.id) || 0);
  };
}

/** "0001 - Smith Farms", or just "Smith Farms" when there is no account number. */
export function accountLabel(accountNo, name, sep = ' - ') {
  return [accountNo, name].map(v => (v == null ? '' : String(v).trim())).filter(Boolean).join(sep);
}
